import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Button from '../../Button';
import { warningsvg } from '../../../assets/svg/svgimages';
import { verifyEmailCode, setAuthRedirectPath, resendConfirmation } from '../../../store/userSlice';

const RESEND_COOLDOWN_SECONDS = 60;

const getCooldownStorageKey = (email) => `confirm-email-resend-available-at:${email || 'unknown'}`;

const getRemainingSeconds = (availableAt) => (
  Math.max(0, Math.ceil((availableAt - Date.now()) / 1000))
);

const getInitialAvailableAt = (storageKey, startResendCooldown) => {
  const storedAvailableAt = Number(sessionStorage.getItem(storageKey));
  if (Number.isFinite(storedAvailableAt) && storedAvailableAt > Date.now()) {
    return storedAvailableAt;
  }

  if (!startResendCooldown) {
    return Date.now();
  }

  const nextAvailableAt = Date.now() + RESEND_COOLDOWN_SECONDS * 1000;
  sessionStorage.setItem(storageKey, String(nextAvailableAt));
  return nextAvailableAt;
};

const ConfirmEmail = ({ startResendCooldown = true }) => {
  const { status, authRedirectPath } = useSelector(state => state.user);
  const email = useSelector(state => state.user.email);
  const cooldownStorageKey = getCooldownStorageKey(email);
  const [resendAvailableAt, setResendAvailableAt] = useState(
    () => getInitialAvailableAt(cooldownStorageKey, startResendCooldown),
  );

  const [formData, setFormData] = useState({ email: email, code: '' });
  const [error, setError] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(
    () => getRemainingSeconds(resendAvailableAt),
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const remaining = getRemainingSeconds(resendAvailableAt);
    setResendCooldown(remaining);
    if (remaining <= 0) return;

    const timer = setTimeout(() => {
      setResendCooldown(getRemainingSeconds(resendAvailableAt));
    }, 1000);

    return () => clearTimeout(timer);
  }, [resendAvailableAt, resendCooldown]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const isResending = status.resendConfirmationStatus === 'loading';

  const handleResend = async () => {
    if (resendCooldown > 0 || isResending) return;
    try {
      await dispatch(resendConfirmation(email)).unwrap();
      const nextAvailableAt = Date.now() + RESEND_COOLDOWN_SECONDS * 1000;
      sessionStorage.setItem(cooldownStorageKey, String(nextAvailableAt));
      setResendAvailableAt(nextAvailableAt);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setError(null);
    } catch (err) {
      setError(err);
    }
  };

  const formatError = (err) => {
    if (!err) return null;
    if (err.message === 'Invalid code or code has expired.') return 'Invalid or expired code — request a new one.';
    if (err.detail) return err.detail;
    return err.message || 'Something went wrong.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await dispatch(verifyEmailCode(formData)).unwrap();
      const target = authRedirectPath || '/';
      navigate(target, { replace: true });
      dispatch(setAuthRedirectPath(null));
    } catch (err) {
      setError(err);
    }
  };

  const resendDisabled = resendCooldown > 0 || isResending;

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <h3 className="h3-medium">Confirm email</h3>
        {email && (
          <p className="form-hint">Code sent to {email}</p>
        )}
        {error && (
          <div className="error-message">
            {warningsvg} {formatError(error)}
          </div>
        )}
        <input
          type="text"
          id="code"
          placeholder="Code from email"
          value={formData.code}
          onChange={handleChange}
        />
        <Button
          variant={status.emailVerificationStatus === 'loading' ? 'grey' : 'blue'}
          iconType="arrowRight"
          label={status.emailVerificationStatus === 'loading' ? 'Confirmation...' : 'Confirm'}
          type="submit"
        />
        <button
          type="button"
          className="resend-code-button"
          onClick={handleResend}
          disabled={resendDisabled}
        >
          {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
        </button>
      </div>
    </form>
  );
};

export default ConfirmEmail;
